import React from 'react';
import { motion } from 'motion/react';
import { TrendingUp, AlertTriangle, Info, CheckCircle2 } from 'lucide-react';

type VantiSummarySpec = {
  headline: string;
  bullets: string[];
  recommendation?: string;
  sentiment?: 'positive' | 'neutral' | 'warning' | 'negative';
};

const SENTIMENT_STYLES = {
  positive: {
    border: 'border-vivid-green-cyan/30',
    bg: 'bg-vivid-green-cyan/5',
    icon: CheckCircle2,
    iconClass: 'text-vivid-green-cyan',
    headlineClass: 'text-vivid-green-cyan',
  },
  neutral: {
    border: 'border-dark-border',
    bg: 'bg-dark-surface',
    icon: Info,
    iconClass: 'text-slate-400',
    headlineClass: 'text-slate-200',
  },
  warning: {
    border: 'border-amber-500/30',
    bg: 'bg-amber-500/5',
    icon: AlertTriangle,
    iconClass: 'text-amber-400',
    headlineClass: 'text-amber-300',
  },
  negative: {
    border: 'border-vivid-red/30',
    bg: 'bg-vivid-red/5',
    icon: TrendingUp,
    iconClass: 'text-vivid-red',
    headlineClass: 'text-vivid-red',
  },
};

export default function VantiSummary({ spec }: { spec: VantiSummarySpec }) {
  if (!spec?.headline) return null;
  const sentiment = spec.sentiment && SENTIMENT_STYLES[spec.sentiment] ? spec.sentiment : 'neutral';
  const styles = SENTIMENT_STYLES[sentiment];
  const Icon = styles.icon;

  return (
    <motion.div
      layout={false}
      className={`my-3 rounded-xl border p-4 ${styles.border} ${styles.bg}`}
    >
      <div className="mb-2.5 flex items-start gap-2.5">
        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${styles.iconClass}`} />
        <p className={`text-sm font-semibold leading-snug ${styles.headlineClass}`}>
          {spec.headline}
        </p>
      </div>

      {spec.bullets?.length > 0 && (
        <ul className="mb-2 space-y-1.5 pl-6">
          {spec.bullets.map((b, i) => (
            <li key={i} className="text-xs leading-relaxed text-slate-300 before:mr-2 before:content-['·']">
              {b}
            </li>
          ))}
        </ul>
      )}

      {spec.recommendation && (
        <div className="mt-3 rounded-lg border border-vivid-purple/20 bg-vivid-purple/8 px-3 py-2">
          <p className="text-xs font-medium text-vivid-purple">Recommendation</p>
          <p className="mt-0.5 text-xs leading-relaxed text-slate-300">{spec.recommendation}</p>
        </div>
      )}
    </motion.div>
  );
}
